import { api, ApiError } from './api';
import { GONE_TEXT, type GoneKind } from './edit-conflict-text';
import { editorToast } from './editor-toast';
import { queryClient } from './query-client';

/**
 * "Someone else deleted what you were saving."
 *
 * Told apart from a plain edit conflict: there is nothing to reload, so the
 * panel says what happened (no name: the API does not record who deleted) and
 * acts locally. See `notifyGone`.
 */
export type { GoneKind };

export { GONE_TEXT };

/** API-side wording carried by `goneError`, mapped to `GONE_TEXT` in errors.ts. */
export const GONE_API_MESSAGE: Record<GoneKind, string> = {
  marker: 'Someone else deleted this marker',
  panorama: 'Someone else deleted this panorama',
  tour: 'Someone else deleted this tour',
};

/** The API's own "not found" message for each kind (what a PATCH/DELETE answers when the row is gone). */
const NOT_FOUND_MESSAGE: Record<GoneKind, string> = {
  marker: 'Marker not found',
  panorama: 'Panorama not found',
  tour: 'Tour not found',
};

/** Classifies a failed save/delete: was the target itself deleted? */
export function goneKindOf(err: unknown): GoneKind | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { code?: unknown; entity?: unknown } | null;
  if (body?.code === 'deleted_by_other') {
    return body.entity === 'marker' || body.entity === 'panorama' || body.entity === 'tour' ? body.entity : null;
  }
  if (err.status !== 404) return null;
  for (const kind of ['marker', 'panorama', 'tour'] as const) {
    if (err.message === NOT_FOUND_MESSAGE[kind]) return kind;
  }
  return null;
}

/** What the caller's mutation sees instead of the raw 404, so its generic toast is recognised and dropped. */
export function goneError(kind: GoneKind): ApiError {
  return new ApiError(404, { code: 'deleted_by_other', entity: kind }, GONE_API_MESSAGE[kind]);
}

/** `DELETE`, but a row that is already gone counts as done: the end state is what the user wanted. */
export async function deleteIfPresent(path: string): Promise<void> {
  try {
    await api.delete(path);
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err;
  }
}

let navigateTo: ((to: string) => void) | null = null;

/** Mounted once under the router (App.tsx) so non-component code can navigate. */
export function registerGoneNavigator(fn: ((to: string) => void) | null): () => void {
  navigateTo = fn;
  return () => {
    if (navigateTo === fn) navigateTo = null;
  };
}

/** `/admin/organizations/:org[/projects/:project[/tours/:tour[/panoramas/:id]]]` -> its slugs. */
export function parseAdminPath(pathname: string) {
  const m = /^\/admin\/organizations\/([^/]+)(?:\/projects\/([^/]+)(?:\/tours\/([^/]+))?)?/.exec(pathname);
  return { orgSlug: m?.[1], projectSlug: m?.[2], tourSlug: m?.[3] };
}

/**
 * Tells the user and acts locally:
 *  - panorama: back to the tour page (the grid refetches);
 *  - tour: back to the organization home;
 *  - marker: the editor removes it itself (see `useMarkerSave`), this only toasts.
 */
export function notifyGone(kind: GoneKind, pathname: string = globalThis.window?.location?.pathname ?? ''): void {
  editorToast.warning(GONE_TEXT[kind], { id: `gone-${kind}`, duration: 8000 });
  const { orgSlug, projectSlug, tourSlug } = parseAdminPath(pathname);
  if (kind === 'panorama') {
    void queryClient.invalidateQueries({ queryKey: ['tour'] });
    if (orgSlug && projectSlug && tourSlug) {
      navigateTo?.(`/admin/organizations/${orgSlug}/projects/${projectSlug}/tours/${tourSlug}`);
    }
  } else if (kind === 'tour') {
    void queryClient.invalidateQueries({ queryKey: ['tour'] });
    void queryClient.invalidateQueries({ queryKey: ['org'] });
    if (orgSlug) navigateTo?.(`/admin/organizations/${orgSlug}`);
  }
}
